/* eslint-disable consistent-return */
import { useState } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../auth/auth.store';
import { refreshAccessToken, redirectToLoginAsExpired, redirectToUnauthorized } from '../../auth/authAxios';
export const baseUrl = process.env.API_BASE_URL;
// Queue for concurrent 401s — holds resolve/reject for each waiting request.
let isRefreshing = false;
let failedQueue = [];
const processQueue = (err, token) => {
    failedQueue.forEach(p => err ? p.reject(err) : p.resolve(token));
    failedQueue = [];
};
// Single shared instance — services are differentiated by path prefix
// (e.g. brand-manager/..., user-manager/..., workflow-manager/...).
const instance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
});
// IANA zone name (e.g. "America/New_York") — lets the BE localize/interpret
// dates using the same timezone the browser is rendering them in.
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
instance.interceptors.request.use((config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Timezone'] = userTimeZone;
    return config;
});
instance.interceptors.response.use((response) => response, async (error) => {
    const config = error.config;
    const status = error.response?.status;
    // 403 — authenticated but insufficient permissions
    if (status === 403) {
        redirectToUnauthorized();
        return Promise.reject(error);
    }
    // 401 — token expired or account deactivated
    if (status === 401) {
        // Retry already fired but still got 401 — session is invalid, force logout
        if (config?._retry) {
            redirectToLoginAsExpired();
            return Promise.reject(error);
        }
        if (config) {
            config._retry = true;
            if (isRefreshing) {
                // Another refresh is already in flight — wait for it then retry
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    config.headers.Authorization = `Bearer ${token}`;
                    return instance(config);
                }).catch(err => Promise.reject(err));
            }
            isRefreshing = true;
            try {
                const refreshed = await refreshAccessToken();
                if (refreshed) {
                    const newToken = useAuthStore.getState().accessToken ?? '';
                    processQueue(null, newToken);
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return instance(config);
                }
                processQueue(error, null);
                redirectToLoginAsExpired();
                return Promise.reject(error);
            }
            finally {
                isRefreshing = false;
            }
        }
    }
    return Promise.reject(error);
});
const useAxios = () => {
    const [apiLoading, setLoading] = useState(false);
    const [apiError, setError] = useState(null);
    const get = async (url, options) => {
        const { delegation, skipSpinner = false } = options || {};
        if (!skipSpinner)
            setLoading(true);
        setError(null);
        try {
            const response = await instance.get(url, {
                headers: {
                    ...(delegation ? { delegation: JSON.stringify(delegation) } : {})
                },
            }).then((res) => {
                const { status } = res;
                if (status && [200, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            if (!skipSpinner)
                setLoading(false);
        }
    };
    const getArrayBuffer = async (url, delegation, timeout) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.get(url, {
                responseType: 'blob',
                // Large media/PDF downloads can exceed the shared 30s default —
                // callers pass timeout: 0 (no limit) for whole-file fetches of
                // files that may be well over 100MB.
                ...(timeout !== undefined ? { timeout } : {}),
                headers: {
                    ...(delegation ? { delegation: JSON.stringify(delegation) } : {})
                },
            }).then((res) => {
                const { status } = res;
                if (status && [200, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const post = async (url, data, options) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.post(url, data && data, options?.timeout ? { timeout: options.timeout } : undefined).then((res) => {
                const { status } = res;
                if (status && [200, 201, 202, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const postArrayBuffer = async (url, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.post(url, data, {
                responseType: 'blob',
            }).then((res) => {
                const { status } = res;
                if (status && [200, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const postMultipart = async (url, data, delegation, extraHeaderParam, configOverrides) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.post(url, data, {
                timeout: 0,
                ...(configOverrides || {}),
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...(delegation ? { delegation: JSON.stringify(delegation) } : {}),
                    ...(extraHeaderParam && extraHeaderParam)
                }
            }).then((res) => {
                const { status } = res;
                if (status && [200, 201, 202, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const put = async (url, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.put(url, data).then((res) => {
                const { status } = res;
                if (status && [200, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const patch = async (url, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.patch(url, data).then((res) => {
                const { status } = res;
                if (status && [200, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const deleteRequest = async (url, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.delete(url, data ? { data } : undefined).then((res) => {
                const { status } = res;
                if (status && [200, 204].includes(status)) {
                    return res;
                }
            });
            return response;
        }
        catch (err) {
            setError(err.response ? err.response.data : err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    return { get, getArrayBuffer, post, postArrayBuffer, postMultipart, put, patch, deleteRequest, apiLoading, apiError };
};
export default useAxios;
